// 네이티브 만보기(걸음 센서) 래퍼 — Android Step Counter 커스텀 플러그인.
// 센서는 "부팅 후 누적 걸음(raw)"을 반환. KST 날짜별 baseline 차감/자정 리셋은
// 백엔드(routes/steps.py)가 권위 소스로 처리한다(여기선 raw 값만 그대로 전달).
// 웹에서는 available=false 로 폴백되어 기존 "임시 입력" UI가 동작.
//
// 플러그인 응답 계약: { steps, available, hasValue }
//   hasValue=false → 아직 실제 센서값을 못 받음. 이때 steps(0)는 무효 → 백엔드로 0을 보내지 않는다.
import { registerPlugin } from "@capacitor/core";
import { isNative, isAndroid } from "./platform";

const StepCounter = registerPlugin("StepCounter");

export const pedometerAvailable = () => isNative() && isAndroid();

export const ensureStepPermission = async () => {
  if (!pedometerAvailable()) return false;
  try {
    let st = await StepCounter.checkPermissions();
    if (st.activity !== "granted") st = await StepCounter.requestPermissions();
    return st.activity === "granted";
  } catch (e) {
    return false;
  }
};

let listenerHandle = null;

// 부팅후 누적 걸음(raw)을 그대로 콜백으로 전달. 반환: cleanup 함수.
export const startRawTracking = async (onCumulative) => {
  if (!pedometerAvailable()) return () => {};
  const ok = await ensureStepPermission();
  if (!ok) return () => {};
  try {
    const res = await StepCounter.start();
    // 실제 센서값을 받았을 때만(hasValue) 전달 — 받기 전 0은 무시.
    if (res && res.hasValue && typeof res.steps === "number") onCumulative(res.steps);
    listenerHandle = await StepCounter.addListener("stepChange", (ev) => {
      if (ev && typeof ev.steps === "number") onCumulative(ev.steps);
    });
  } catch (e) {
    return () => {};
  }
  return stopStepTracking;
};

export const stopStepTracking = async () => {
  try { if (listenerHandle) { await listenerHandle.remove(); listenerHandle = null; } } catch (e) { /* noop */ }
  try { await StepCounter.stop(); } catch (e) { /* noop */ }
};

// 현재 부팅후 누적 걸음(raw). 아직 실제 센서값을 못 받았으면(hasValue=false) null.
export const getRawCumulative = async () => {
  if (!pedometerAvailable()) return null;
  try {
    const res = await StepCounter.getCurrent();
    // hasValue=false면 steps(0)는 무효 → null 반환(백엔드에 0 전송/덮어쓰기 방지).
    if (res && res.hasValue && typeof res.steps === "number") return res.steps;
  } catch (e) { /* noop */ }
  return null;
};

// 앱이 확정한 "오늘 걸음"(백엔드 KST 진실)을 네이티브 서비스로 전달 → 상시 알림을 화면과 통일.
// 서버 동기화 직후 호출. 웹/미지원 no-op. 서비스 전용 키(appTodaySteps/appTodayCum/appTodayDate)에 저장.
export const setTodaySteps = async (todaySteps, cumulativeAtSync) => {
  if (!pedometerAvailable()) return;
  if (todaySteps == null || cumulativeAtSync == null) return;
  try {
    await StepCounter.setTodaySteps({
      todaySteps: Math.max(0, Math.round(todaySteps)),
      cumulativeAtSync: Math.max(0, Math.round(cumulativeAtSync)),
    });
  } catch (e) { /* noop */ }
};

// 상시 알림 문구 템플릿을 네이티브 서비스로 전달(서비스 전용 SharedPreferences 키에 저장).
// 웹/미지원은 no-op. 값은 관리자 정책(steps_notif_template)에서 내려온 문자열. "{steps}" 토큰 지원.
export const setNotifTemplate = async (template) => {
  if (!pedometerAvailable()) return;
  try { await StepCounter.setNotifTemplate({ template: String(template || "") }); } catch (e) { /* noop */ }
};

// 서비스가 유지하는 "기기 로컬 자정 시점 센서 누적값". 백엔드 오늘 첫 기록 baseline 교정용.
// 웹/구버전 APK(메서드 없음)/실패/미보유면 null → 호출부는 기존 로직으로 폴백.
export const getDayBaseline = async () => {
  if (!pedometerAvailable()) return null;
  try {
    const res = await StepCounter.getDayBaseline();
    if (res && res.hasBaseline && typeof res.baseline === "number" && res.baseline >= 0) {
      return { baseline: res.baseline, baselineDate: res.baselineDate };
    }
  } catch (e) { /* 구버전 APK 등 → 폴백 */ }
  return null;
};

// 걸음 권한 보유 여부만 확인(요청 팝업 없음) — 온보딩 게이팅용.
export const hasStepPermission = async () => {
  if (!pedometerAvailable()) return false;
  try {
    const st = await StepCounter.checkPermissions();
    return st.activity === "granted";
  } catch (e) { return false; }
};

// 배터리 최적화 예외(제외) 여부. 웹/미지원/판별실패는 true로 간주(안내 미표시 → 스팸 방지).
export const isBatteryOptimizationExempt = async () => {
  if (!pedometerAvailable()) return true;
  try {
    const res = await StepCounter.isBatteryOptimizationExempt();
    return !!(res && res.exempt);
  } catch (e) { return true; }
};

// 시스템 배터리 최적화 예외 허용 팝업 실행. 반환: { opened, fallback }.
export const requestBatteryOptimizationExemption = async () => {
  if (!pedometerAvailable()) return { opened: false, fallback: false };
  try {
    const res = await StepCounter.requestBatteryOptimizationExemption();
    return { opened: !!(res && res.opened), fallback: !!(res && res.fallback) };
  } catch (e) { return { opened: false, fallback: false }; }
};
